import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useYearbook } from '../context/YearbookContext';
import { useFunnyLoadingText } from '../hooks';
import { 
  generatePDF, 
  downloadPDF, 
  getSessionSecret, 
  fetchCoverAsBlob, 
  fetchStyledCoverAsBlob,
  getPurchaseStatus,
  generateFullPDF,
  downloadFullPDF,
} from '../api/client';
import { Analytics } from '../utils/analytics';
import { ChapterList, StoryViewer, LeadCaptureModal, MobileBottomNav, TiltedBookMockup, PayPalCheckout } from '../components/features';
import { MobileDrawer } from '../components/common';
import { getAvatarGradient, getInitials, registerParticipants } from '../utils/nameColors';
import {
  ArrowPathIcon,
  BookOpenIcon,
  SparklesIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  DocumentArrowDownIcon,
  ArrowDownTrayIcon,
  UserGroupIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  GiftIcon,
  StarIcon,
  BookmarkIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';

// Helper: Get trait badge color based on keyword (earthy vintage palette)
const getTraitColor = (trait) => {
  const traitLower = (trait || '').toLowerCase();
  // Funny/witty → Amber/gold (warm honey tones)
  if (['funny', 'humorous', 'jokester', 'comedian', 'witty'].some(t => traitLower.includes(t))) {
    return 'bg-amber-50 text-amber-800 border-amber-300/50';
  }
  // Adventurer → Sage/olive (muted green)
  if (['adventurer', 'explorer', 'traveler', 'adventurous'].some(t => traitLower.includes(t))) {
    return 'bg-emerald-900/10 text-emerald-800 border-emerald-700/20';
  }
  // Storyteller → Terracotta/rust (warm clay)
  if (['storyteller', 'narrator', 'creative'].some(t => traitLower.includes(t))) {
    return 'bg-orange-100/80 text-orange-800 border-orange-300/50';
  }
  // Thinker → Slate/stone (cool neutral)
  if (['thinker', 'wise', 'thoughtful', 'insightful', 'philosopher'].some(t => traitLower.includes(t))) {
    return 'bg-stone-200/60 text-stone-700 border-stone-400/40';
  }
  // Optimist → Wheat/sand (light warm)
  if (['optimist', 'dreamer', 'enthusiast', 'positive'].some(t => traitLower.includes(t))) {
    return 'bg-yellow-100/60 text-yellow-800 border-yellow-400/40';
  }
  // Peacemaker → Dusty rose (muted pink)
  if (['peacemaker', 'mediator', 'supportive', 'empathetic', 'caring'].some(t => traitLower.includes(t))) {
    return 'bg-rose-100/60 text-rose-700 border-rose-300/50';
  }
  // Observant → Bronze/sepia (warm brown)
  if (['observant', 'perceptive', 'analytical'].some(t => traitLower.includes(t))) {
    return 'bg-amber-100/50 text-amber-900 border-amber-400/40';
  }
  // Default: parchment/sepia (matches site theme)
  return 'bg-parchment/60 text-sepia border-sepia/20';
};

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function PreviewPage() {
  const navigate = useNavigate();
  const { 
    isInitialized,
    sessionId,
    getBuildConfig,
    bookTitle,
    bookSubtitle,
    bookDedication,
    stats,
  } = useYearbook();
  
  // State
  const [status, setStatus] = useState('idle'); // idle, loading, toc_ready, generating, complete, error
  const [progress, setProgress] = useState({ stage: '', message: '', current: 0, total: 0 });
  const funnyLoadingText = useFunnyLoadingText();
  const [chapters, setChapters] = useState([]);
  const [stories, setStories] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [charactersLoading, setCharactersLoading] = useState(false);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [error, setError] = useState(null);
  const [showCharacters, setShowCharacters] = useState(true);
  const [expandedCharacter, setExpandedCharacter] = useState(null); // Track which character card is expanded
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [coverImage, setCoverImage] = useState(null);
  
  // Purchase state
  const [showPayPalModal, setShowPayPalModal] = useState(false);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [fullPdfStatus, setFullPdfStatus] = useState('idle'); // idle, generating, ready, error
  const [fullPdfError, setFullPdfError] = useState(null);
  const [isDownloadingFull, setIsDownloadingFull] = useState(false);
  
  // All narratives for full book (includes non-preview stories)
  const [allNarratives, setAllNarratives] = useState([]);
  
  // Mobile navigation state
  const [mobileTab, setMobileTab] = useState('story'); // story, chapters, cast
  const [chaptersDrawerOpen, setChaptersDrawerOpen] = useState(false);
  const [castDrawerOpen, setCastDrawerOpen] = useState(false);
  
  // Handle mobile tab changes
  const handleMobileTabChange = (tab) => {
    setMobileTab(tab);
    if (tab === 'chapters') {
      setChaptersDrawerOpen(true);
    } else if (tab === 'cast') {
      setCastDrawerOpen(true);
    }
  };
  
  // Close drawer and reset to story tab
  const handleCloseChaptersDrawer = () => {
    setChaptersDrawerOpen(false);
    setMobileTab('story');
  };
  
  const handleCloseCastDrawer = () => {
    setCastDrawerOpen(false);
    setMobileTab('story');
  };
  
  // Handle story selection from mobile drawer
  const handleMobileStorySelect = (index) => {
    handleStoryNavigate(index);
    handleCloseChaptersDrawer();
  };
  
  // Track story navigation
  const handleStoryNavigate = (index) => {
    setCurrentStoryIndex(index);
    if (stories[index]) {
      Analytics.trackStoryViewed(index, stories[index].title || 'Untitled');
    }
  };
  
  // PDF generation state
  const [pdfStatus, setPdfStatus] = useState('idle'); // idle, generating, ready, error
  const [pdfError, setPdfError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  
  // Guard against duplicate requests (React StrictMode runs effects twice)
  // Track the session+year combo we've started preview for
  const startedForRef = useRef(null);
  
  // Start preview generation on mount
  useEffect(() => {
    // Wait for initialization before checking session
    if (!isInitialized) {
      return;
    }
    
    // Redirect to home if no valid session
    if (!sessionId) {
      navigate('/');
      return;
    }

    // Build a key for this session
    const previewKey = sessionId;

    // Prevent duplicate requests from StrictMode, but ALLOW if session changed
    if (startedForRef.current === previewKey) {
      return;
    }
    startedForRef.current = previewKey;
    
    // Start the preview
    const runPreview = async () => {
      setStatus('loading');
      setProgress({ stage: 'connecting', message: 'Starting preview...', current: 0, total: 0 });
      setError(null);
      // Reset chapters and stories to prevent leaking between sessions
      setChapters([]);
      setStories([]);
      setCurrentStoryIndex(0);
      // Set characters loading from the start so the UI shows loading state immediately
      setCharactersLoading(true);
      setCharacters([]);
      
      try {
        const config = getBuildConfig();
        
        // Use fetch with ReadableStream for SSE
        const sessionSecret = getSessionSecret();
        const response = await fetch(`${API_BASE_URL}/api/session/${sessionId}/preview`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(sessionSecret && { 'Authorization': `Bearer ${sessionSecret}` }),
          },
          body: JSON.stringify({ config }),
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        // Track SSE event state across chunks (events may be split across network reads)
        let currentEvent = null;
        let currentData = '';
        
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) break;
          
          buffer += decoder.decode(value, { stream: true });
          
          // Process complete events in buffer
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer
          
          for (const line of lines) {
            // Trim to handle \r from nginx/proxies
            const trimmedLine = line.trim();
            
            // Skip empty lines and retry directives
            if (!trimmedLine || trimmedLine.startsWith('retry:')) {
              continue;
            }
            
            if (trimmedLine.startsWith('event:')) {
              currentEvent = trimmedLine.slice(6).trim();
            } else if (trimmedLine.startsWith('data:')) {
              currentData = trimmedLine.slice(5).trim();
              
              if (currentEvent && currentData) {
                try {
                  const data = JSON.parse(currentData);
                  handleSSEEvent(currentEvent, data);
                } catch (e) {
                  console.error('Failed to parse SSE data:', e, 'Event:', currentEvent);
                }
                currentEvent = null;
                currentData = '';
              }
            }
          }
        }
        
      } catch (err) {
        console.error('Preview error:', err);
        setError(err.message || 'Failed to generate preview');
        setStatus('error');
      }
    };
    
    runPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, sessionId, navigate]);
  
  // Load cover image for book mockup (prefer styled cover if available)
  useEffect(() => {
    if (!sessionId) return;
    
    let isMounted = true;
    const loadCover = async () => {
      // First try to get the styled cover
      try {
        const { url: styledUrl } = await fetchStyledCoverAsBlob(sessionId);
        if (isMounted && styledUrl) {
          setCoverImage(styledUrl);
          return;
        }
      } catch (err) {
        // No styled cover - try original
        console.log('No styled cover available, trying original');
      }
      
      // Fall back to original cover
      try {
        const blobUrl = await fetchCoverAsBlob(sessionId);
        if (isMounted) {
          setCoverImage(blobUrl);
        }
      } catch (err) {
        // No cover uploaded - that's fine, we'll show placeholder
        console.log('No cover image available');
      }
    };
    
    loadCover();
    
    return () => {
      isMounted = false;
    };
  }, [sessionId]);
  
  // Check purchase status on mount
  useEffect(() => {
    if (!sessionId) return;
    
    getPurchaseStatus(sessionId)
      .then((data) => {
        if (data.purchased) {
          setHasPurchased(true);
        }
      })
      .catch((err) => {
        console.log('Could not check purchase status:', err);
      });
  }, [sessionId]);
  
  const handleSSEEvent = (event, data) => {
    switch (event) {
      case 'progress':
        setProgress({
          stage: data.stage || '',
          message: data.message || '',
          current: data.current || 0,
          total: data.total || 0,
        });
        if (data.stage === 'generating') {
          setStatus('generating');
        }
        if (data.stage === 'characters') {
          setCharactersLoading(true);
        }
        break;
      
      case 'toc_ready':
        setChapters(data.chapters || []);
        setStatus('toc_ready');
        setProgress({ stage: 'toc', message: `Found ${data.total_chapters} chapters`, current: 0, total: data.sample_count });
        break;
      
      case 'story_ready':
        console.log('[SSE] story_ready received:', {
          story_id: data.story?.story_id,
          chapter_index: data.story?.chapter_index,
          illustration_loading: data.story?.illustration_loading,
          is_preview: data.story?.is_preview,
        });
        // Only add to visible stories if it's a preview story
        if (data.story?.is_preview !== false) {
          setStories(prev => [...prev, data.story]);
        }
        // Always add to allNarratives for full book generation
        setAllNarratives(prev => [...prev, data.story]);
        // Update the chapter title and emoji with AI-generated values
        if (data.story?.chapter_index !== undefined && data.story?.title) {
          setChapters(prev => prev.map(chapter => 
            chapter.index === data.story.chapter_index 
              ? { ...chapter, title: data.story.title, title_emoji: data.story.title_emoji }
              : chapter
          ));
        }
        setProgress(prev => ({
          ...prev,
          current: prev.current + 1,
        }));
        break;
      
      case 'title_ready':
        // Update chapter title and emoji for non-preview stories
        if (data.chapter_index !== undefined) {
          setChapters(prev => prev.map(chapter => 
            chapter.index === data.chapter_index 
              ? { ...chapter, title: data.title, title_emoji: data.title_emoji }
              : chapter
          ));
        }
        break;
      
      case 'illustration_ready':
        // Update the story with the illustration URL
        console.log('[SSE] illustration_ready received:', {
          story_id: data.story_id,
          chapter_index: data.chapter_index,
          has_url: !!data.illustration_url,
          error: data.error
        });
        setStories(prev => {
          const updated = prev.map(story => {
            if (story.story_id === data.story_id) {
              console.log('[SSE] Matched story:', story.story_id, '-> setting illustration_loading: false');
              return { ...story, illustration_url: data.illustration_url, illustration_loading: false };
            }
            return story;
          });
          // Check if we found a match
          const matchedCount = prev.filter(s => s.story_id === data.story_id).length;
          if (matchedCount === 0) {
            console.warn('[SSE] No matching story found for story_id:', data.story_id, 'Available IDs:', prev.map(s => s.story_id));
          }
          return updated;
        });
        break;
      
      case 'characters_ready':
        console.log('[SSE] characters_ready received:', data.characters?.length, 'characters');
        // Register participants for color assignment (by order, not hash)
        if (data.characters && data.characters.length > 0) {
          registerParticipants(data.characters.map(c => c.name));
        }
        setCharacters(data.characters || []);
        setCharactersLoading(false);
        // Auto-expand the first character
        if (data.characters && data.characters.length > 0) {
          setExpandedCharacter(0);
        }
        break;
      
      case 'complete':
        // Use final chapters from complete event (includes all AI-generated titles)
        console.log('[Complete] Received chapters:', data.chapters?.length, 'First:', data.chapters?.[0]);
        console.log('[Complete] Received stories:', data.stories?.length, 'Current stories:', stories.length);
        if (data.chapters && data.chapters.length > 0) {
          setChapters(data.chapters);
        }
        // Use final characters from complete event
        if (data.characters && data.characters.length > 0) {
          registerParticipants(data.characters.map(c => c.name));
          setCharacters(data.characters);
        }
        // Defensive fix: ensure ALL stories have illustration_loading set to false
        // This handles any timing issues where illustration_ready events might have been missed
        setStories(prev => {
          // If story_ready events failed to stream, fall back to stories from complete event
          if (prev.length === 0 && data.stories && data.stories.length > 0) {
            console.log('[Complete] Using stories from complete event (streaming may have failed)');
            return data.stories.map(story => ({ ...story, illustration_loading: false }));
          }
          return prev.map(story => ({
            ...story,
            illustration_loading: false,
          }));
        });
        setCharactersLoading(false);
        setStatus('complete');
        // Track preview viewed
        Analytics.trackPreviewViewed();
        break;
      
      case 'error':
        setError(data.message || 'An error occurred');
        setStatus('error');
        break;
      
      default:
        console.log('Unknown SSE event:', event, data);
    }
  };
  
  const handleRetry = () => {
    // Reload the page to restart preview
    window.location.reload();
  };
  
  const handleGeneratePDF = async () => {
    setPdfStatus('generating');
    setPdfError(null);
    
    try {
      const buildConfig = getBuildConfig();
      const config = {
        title: bookTitle || 'Yearbook',
        subtitle: bookSubtitle || null,
        dedication: bookDedication || null,
        yearbook_type: buildConfig.yearbook_type || 'group',
      };
      
      console.log('[PDF] Generating PDF with:', { 
        stories: stories.length, 
        chapters: chapters.length,
        characters: characters.length,
        sampleChapters: chapters.filter(c => c.is_sample).length,
        firstChapter: chapters[0],
      });
      await generatePDF(sessionId, config, stories, chapters, characters);
      setPdfStatus('ready');
    } catch (err) {
      console.error('PDF generation error:', err);
      setPdfError(err.response?.data?.detail || 'Failed to generate PDF. Please try again.');
      setPdfStatus('error');
    }
  };
  
  // Handle purchase completion
  const handlePurchaseComplete = () => {
    setHasPurchased(true);
    setShowPayPalModal(false);
    // Automatically start generating the full PDF
    handleGenerateFullPDF();
  };
  
  // Generate full PDF after purchase
  const handleGenerateFullPDF = async () => {
    setFullPdfStatus('generating');
    setFullPdfError(null);
    
    try {
      const buildConfig = getBuildConfig();
      const config = {
        title: bookTitle || 'Yearbook',
        subtitle: bookSubtitle || null,
        dedication: bookDedication || null,
        yearbook_type: buildConfig.yearbook_type || 'group',
      };
      
      // Use all narratives for full book (not just preview stories)
      const storiesToUse = allNarratives.length > 0 ? allNarratives : stories;
      
      console.log('[Full PDF] Generating full PDF with:', { 
        stories: storiesToUse.length, 
        chapters: chapters.length,
        characters: characters.length,
      });
      
      await generateFullPDF(sessionId, config, storiesToUse, chapters, characters);
      setFullPdfStatus('ready');
      Analytics.trackFullBookGenerated(chapters.length);
    } catch (err) {
      console.error('Full PDF generation error:', err);
      setFullPdfError(err.response?.data?.detail || 'Failed to generate full PDF. Please try again.');
      setFullPdfStatus('error');
    }
  };
  
  // Download full PDF
  const handleDownloadFullPDF = async () => {
    if (isDownloadingFull) return;
    setIsDownloadingFull(true);
    setFullPdfError(null);
    
    try {
      const filename = `${bookTitle || 'Yearbook'}_full.pdf`;
      await downloadFullPDF(sessionId, filename);
      Analytics.trackFullBookDownloaded();
    } catch (err) {
      console.error('Full PDF download failed:', err);
      setFullPdfError('Download failed. Please try again.');
    } finally {
      setIsDownloadingFull(false);
    }
  };
  
  // Loading/Progress view
  if (status === 'idle' || status === 'loading' || (status === 'toc_ready' && stories.length === 0)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-rust/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <ArrowPathIcon className="w-10 h-10 text-rust animate-spin" />
          </div>
          <h2 className="font-display text-2xl font-bold text-ink mb-2">
            Generating Preview
          </h2>
          <p className="text-sepia mb-2">{progress.message || 'Please wait...'}</p>
          <p className="text-sm text-sepia/70 italic mb-6">{funnyLoadingText}</p>
          
          {/* Progress bar for story generation */}
          {progress.total > 0 && (
            <div className="w-full max-w-xs mx-auto">
              <div className="flex justify-between text-sm text-sepia mb-2">
                <span>Stories</span>
                <span>{progress.current} / {progress.total}</span>
              </div>
              <div className="h-2 bg-parchment rounded-full overflow-hidden">
                <div 
                  className="h-full bg-rust transition-all duration-300"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // Error view
  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-rust/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <ExclamationTriangleIcon className="w-10 h-10 text-rust" />
          </div>
          <h2 className="font-display text-2xl font-bold text-ink mb-2">
            Preview Failed
          </h2>
          <p className="text-sepia mb-6">{error}</p>
          <button onClick={handleRetry} className="btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  // Get current story for mobile header
  const currentStory = stories[currentStoryIndex];
  const canGoPrev = currentStoryIndex > 0;
  const canGoNext = currentStoryIndex < stories.length - 1;
  
  // Preview ready view
  return (
    <div className="min-h-screen bg-cream">
      {/* Desktop Header - hidden on mobile */}
      <header className="hidden lg:block bg-white border-b border-parchment sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-rust/10 rounded-lg flex items-center justify-center">
                <BookOpenIcon className="w-5 h-5 text-rust" />
              </div>
              <div>
                <h1 className="font-display text-xl font-bold text-ink">
                  {bookTitle || 'Yearbook'} Preview
                </h1>
                <p className="text-sm text-sepia">
                  {chapters.length} chapters • {stories.length} sample stories
                </p>
              </div>
            </div>
            
            {status === 'complete' && (
              <div className="flex items-center gap-2 text-forest">
                <CheckCircleIcon className="w-5 h-5" />
                <span className="text-sm font-medium">Preview Ready</span>
              </div>
            )}
          </div>
        </div>
      </header>
      
      {/* Mobile Header - compact story navigation */}
      <header className="lg:hidden bg-white border-b border-parchment sticky top-0 z-10 overflow-hidden">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between gap-2 max-w-full">
            {/* Story Title with Emoji */}
            <div className="flex-1 min-w-0">
              <h1 className="font-display text-base font-bold text-ink truncate">
                {currentStory?.title_emoji && <span className="mr-1.5">{currentStory.title_emoji}</span>}
                {currentStory?.title || 'Loading...'}
              </h1>
            </div>
            
            {/* Navigation + Status */}
            <div className="flex items-center gap-2 shrink-0">
              {/* Prev/Next with counter */}
              <div className="flex items-center gap-1 bg-parchment/50 rounded-lg px-1">
                <button
                  onClick={() => canGoPrev && handleStoryNavigate(currentStoryIndex - 1)}
                  disabled={!canGoPrev}
                  className={`p-1.5 rounded transition-colors ${canGoPrev ? 'text-sepia hover:text-ink' : 'text-sepia/30'}`}
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                </button>
                <span className="text-xs font-medium text-sepia px-1 min-w-[3rem] text-center">
                  {currentStoryIndex + 1} / {stories.length}
                </span>
                <button
                  onClick={() => canGoNext && handleStoryNavigate(currentStoryIndex + 1)}
                  disabled={!canGoNext}
                  className={`p-1.5 rounded transition-colors ${canGoNext ? 'text-sepia hover:text-ink' : 'text-sepia/30'}`}
                >
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              </div>
              
              {/* Ready badge */}
              {status === 'complete' && (
                <div className="flex items-center gap-1 text-forest bg-forest/10 px-2 py-1 rounded-full">
                  <CheckCircleIcon className="w-3.5 h-3.5" />
                  <span className="text-[10px] font-semibold">Ready</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      
      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 py-6 lg:pb-6 pb-24">
        <div className="grid lg:grid-cols-12 gap-6">
          {/* Left: Table of Contents - DESKTOP ONLY */}
          <div className="hidden lg:block lg:col-span-4 space-y-4">
            {/* Cast of Characters */}
            {(characters.length > 0 || charactersLoading || stories.length > 0) && (
              <div className="card">
                <button
                  onClick={() => setShowCharacters(!showCharacters)}
                  className="w-full flex items-center justify-between p-4 hover:bg-parchment/50 rounded-lg transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${charactersLoading ? 'bg-gold/20' : 'bg-gold/10'}`}>
                      {charactersLoading ? (
                        <ArrowPathIcon className="w-4 h-4 text-gold animate-spin" />
                      ) : (
                        <UserGroupIcon className="w-4 h-4 text-gold" />
                      )}
                    </div>
                    <div className="text-left">
                      <h3 className="font-display font-semibold text-ink">Cast of Characters</h3>
                      <p className="text-xs text-sepia">
                        {characters.length > 0 
                          ? `${characters.length} people` 
                          : charactersLoading 
                            ? 'Analyzing stories...' 
                            : stats?.participants?.length 
                              ? `${stats.participants.length} people`
                              : 'Coming soon...'}
                      </p>
                    </div>
                  </div>
                  {showCharacters ? (
                    <ChevronUpIcon className="w-5 h-5 text-sepia" />
                  ) : (
                    <ChevronDownIcon className="w-5 h-5 text-sepia" />
                  )}
                </button>
                
                {showCharacters && (
                  <div className="px-3 pb-3 space-y-1.5 max-h-[50vh] overflow-y-auto">
                    {characters.length > 0 ? (
                      // Compact expandable character cards
                      (() => {
                        const maxStoryCount = Math.max(...characters.map(c => c.story_count || 0));
                        return characters.map((char, index) => {
                          const isTopContributor = char.story_count === maxStoryCount && maxStoryCount > 1;
                          const isExpanded = expandedCharacter === index;
                          
                          return (
                            <div 
                              key={index}
                              className={`bg-white rounded-lg border transition-all duration-300 ease-out animate-fade-in-up
                                         ${isExpanded 
                                           ? 'border-gold/40 shadow-md' 
                                           : 'border-parchment/30 hover:border-parchment/60'}`}
                              style={{ animationDelay: `${index * 60}ms` }}
                            >
                              {/* Compact Header Row - Always Visible */}
                              <button
                                onClick={() => setExpandedCharacter(isExpanded ? null : index)}
                                className="w-full flex items-center gap-2.5 p-2.5 text-left"
                              >
                                {/* Small Gradient Avatar */}
                                <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getAvatarGradient(char.name)} 
                                                flex items-center justify-center text-white font-bold text-xs shrink-0
                                                shadow-sm`}>
                                  {getInitials(char.name)}
                                </div>
                                
                                {/* Name */}
                                <span className="font-display text-sm font-semibold text-ink truncate min-w-0">
                                  {char.name}
                                </span>
                                
                                {/* Trait Badges - Compact */}
                                {char.traits && char.traits.length > 0 && (
                                  <div className="hidden sm:flex items-center gap-1 flex-1 min-w-0 overflow-hidden">
                                    {char.traits.slice(0, 2).map((trait, i) => (
                                      <span 
                                        key={i}
                                        className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full border whitespace-nowrap ${getTraitColor(trait)}`}
                                      >
                                        {trait}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                
                                {/* Expand/Collapse Chevron */}
                                <ChevronDownIcon 
                                  className={`w-4 h-4 text-sepia/40 shrink-0 transition-transform duration-200
                                             ${isExpanded ? 'rotate-180' : ''}`} 
                                />
                              </button>
                              
                              {/* Expanded Details */}
                              {isExpanded && (
                                <div className="px-3 pb-3 pt-0 border-t border-parchment/20 animate-fade-in-up">
                                  {/* Signature Quote */}
                                  {char.signature_quote && (
                                    <p className="font-handwritten text-sm text-sepia/70 italic mt-2 pl-2 border-l-2 border-gold/40">
                                      "{char.signature_quote}"
                                    </p>
                                  )}
                                  
                                  {/* Bio */}
                                  <p className="text-xs text-sepia/80 leading-relaxed mt-2">{char.bio}</p>
                                  
                                  {/* Footer: Story count + MVP + All Traits */}
                                  <div className="flex items-center justify-between mt-3 pt-2 border-t border-parchment/20">
                                    <div className="flex items-center gap-2">
                                      <span className="text-[10px] text-sepia/50">
                                        {char.story_count} {char.story_count === 1 ? 'story' : 'stories'}
                                      </span>
                                      {isTopContributor && (
                                        <span className="flex items-center gap-0.5 text-[9px] font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full border border-amber-200">
                                          <StarIcon className="w-2.5 h-2.5" />
                                          MVP
                                        </span>
                                      )}
                                    </div>
                                    
                                    {/* All Traits on Mobile */}
                                    {char.traits && char.traits.length > 0 && (
                                      <div className="flex flex-wrap gap-1 justify-end">
                                        {char.traits.slice(0, 3).map((trait, i) => (
                                          <span 
                                            key={i}
                                            className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full border ${getTraitColor(trait)}`}
                                          >
                                            {trait}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        });
                      })()
                    ) : (
                      // Loading/Pending state - show participant names as placeholders
                      <div className="p-3 space-y-1.5">
                        {stats?.participants && stats.participants.length > 0 ? (
                          // Show participant names while waiting
                          stats.participants.map((name, index) => (
                            <div 
                              key={index}
                              className={`flex items-center gap-2.5 p-2.5 bg-white/50 rounded-lg border border-parchment/20
                                         ${charactersLoading ? 'animate-pulse' : ''}`}
                            >
                              {/* Gradient Avatar placeholder */}
                              <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getAvatarGradient(name)} 
                                              flex items-center justify-center text-white font-bold text-xs shrink-0
                                              shadow-sm ${charactersLoading ? 'opacity-70' : 'opacity-50'}`}>
                                {getInitials(name)}
                              </div>
                              {/* Name */}
                              <span className={`font-display text-sm text-ink truncate min-w-0
                                              ${charactersLoading ? 'opacity-70' : 'opacity-50'}`}>
                                {name}
                              </span>
                            </div>
                          ))
                        ) : (
                          // Fallback if no participants data
                          <div className="p-4 text-center">
                            {charactersLoading ? (
                              <>
                                <ArrowPathIcon className="w-6 h-6 text-gold animate-spin mx-auto mb-2" />
                                <p className="text-xs text-sepia">Building character profiles...</p>
                              </>
                            ) : (
                              <p className="text-xs text-sepia/60 italic">Coming after stories...</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            <ChapterList
              chapters={chapters}
              stories={stories}
              currentStoryIndex={currentStoryIndex}
              onSelectStory={handleStoryNavigate}
            />
          </div>
          
          {/* Right: Story Viewer */}
          <div className="lg:col-span-8 min-w-0">
            {stories.length > 0 ? (
              <StoryViewer
                stories={stories}
                currentIndex={currentStoryIndex}
                onNavigate={handleStoryNavigate}
                allParticipants={characters.map(c => c.name)}
              />
            ) : (
              <div className="card h-96 flex items-center justify-center">
                <div className="text-center">
                  <SparklesIcon className="w-12 h-12 text-sepia/30 mx-auto mb-4 animate-pulse" />
                  <p className="text-sepia mb-2">Generating stories...</p>
                  <p className="text-sm text-sepia/70 italic">{funnyLoadingText}</p>
                </div>
              </div>
            )}
            
            {/* Desktop CTAs - hidden on mobile */}
            <div className="hidden lg:block">
              {status === 'complete' && (
                <div className="mt-6 space-y-4">
                  {/* Primary CTA: Free Sample */}
                  <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-center gap-6">
                      {/* Book mockup - small and centered */}
                      <div className="shrink-0">
                        <TiltedBookMockup 
                          coverImage={coverImage} 
                          title={bookTitle || 'Yearbook'}
                          size="xs"
                        />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h3 className="font-display text-xl font-bold text-gray-900 mb-1">
                          {pdfStatus === 'ready' ? 'Your free sample is ready!' : 'Love what you see?'}
                        </h3>
                        <p className="text-gray-500 text-sm">
                          {pdfStatus === 'ready' 
                            ? 'Download your free PDF sample' 
                            : `Get a free PDF sample with ${stories.length} stories`
                          }
                        </p>
                        {/* Benefit row */}
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                          <span className="flex items-center gap-1">
                            <span className="text-green-500">✓</span> {stories.length} stories
                          </span>
                          <span className="flex items-center gap-1">
                            <span>🕐</span> 30 sec
                          </span>
                          <span className="flex items-center gap-1">
                            <span>📄</span> PDF download
                          </span>
                          <span className="text-gray-300">•</span>
                          <span className="text-gray-400">No signup needed</span>
                        </div>
                        {pdfError && (
                          <p className="text-sm text-rust mt-1">{pdfError}</p>
                        )}
                      </div>
                      
                      <div className="shrink-0">
                        {pdfStatus === 'idle' && (
                          <button 
                            onClick={handleGeneratePDF}
                            className="bg-gradient-to-b from-rust to-rust/90 hover:from-rust/95 hover:to-rust/85 text-white font-semibold px-7 py-3.5 rounded-xl flex items-center gap-2 transition-all shadow-lg shadow-rust/25"
                          >
                            Get Free Sample
                            <span className="text-lg">→</span>
                          </button>
                        )}
                        
                        {pdfStatus === 'generating' && (
                          <button 
                            disabled
                            className="bg-rust/70 text-white font-semibold px-7 py-3.5 rounded-xl flex items-center gap-2 cursor-not-allowed"
                          >
                            <ArrowPathIcon className="w-5 h-5 animate-spin" />
                            Generating...
                          </button>
                        )}
                        
                        {pdfStatus === 'ready' && (
                          <button 
                            onClick={async () => {
                              if (isDownloading) return;
                              setIsDownloading(true);
                              setPdfError(null);
                              Analytics.trackPdfDownloaded();
                              try {
                                const filename = `${bookTitle || 'Yearbook'}.pdf`;
                                await downloadPDF(sessionId, filename);
                              } catch (err) {
                                console.error('Download failed:', err);
                                setPdfError('Download failed. Please try again.');
                              } finally {
                                setIsDownloading(false);
                              }
                            }}
                            disabled={isDownloading}
                            className={`bg-gradient-to-b from-rust to-rust/90 hover:from-rust/95 hover:to-rust/85 text-white font-semibold px-7 py-3.5 rounded-xl flex items-center gap-2 transition-all shadow-lg shadow-rust/25 ${isDownloading ? 'opacity-80 cursor-wait' : ''}`}
                          >
                            {isDownloading ? (
                              <>
                                <ArrowPathIcon className="w-5 h-5 animate-spin" />
                                Downloading...
                              </>
                            ) : (
                              <>
                                <ArrowDownTrayIcon className="w-5 h-5" />
                                Download PDF
                              </>
                            )}
                          </button>
                        )}
                        
                        {pdfStatus === 'error' && (
                          <button 
                            onClick={handleGeneratePDF}
                            className="bg-gradient-to-b from-rust to-rust/90 hover:from-rust/95 hover:to-rust/85 text-white font-semibold px-7 py-3.5 rounded-xl flex items-center gap-2 transition-all shadow-lg shadow-rust/25"
                          >
                            <ArrowPathIcon className="w-5 h-5" />
                            Try Again
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Secondary CTAs: Full Book + Printed Copy - side by side */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Full Book Card */}
                    <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-5 flex flex-col">
                      {/* Most popular badge */}
                      {!hasPurchased && (
                        <div className="mb-2">
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full">
                            <span>⭐</span> Most popular
                          </span>
                        </div>
                      )}
                      <h4 className="font-display text-lg font-bold text-gray-900 mb-1">
                        {hasPurchased 
                          ? (fullPdfStatus === 'ready' ? 'Your full book is ready!' : 'Generating your book...')
                          : 'Unlock the full yearbook'
                        }
                      </h4>
                      <p className="text-gray-500 text-sm mb-1">
                        {hasPurchased 
                          ? `Download all ${chapters.length} chapters`
                          : `All ${chapters.length} chapters • Full resolution • Instant download`
                        }
                      </p>
                      {fullPdfError && (
                        <p className="text-sm text-rust mb-2">{fullPdfError}</p>
                      )}
                      <div className="flex-1"></div>
                      
                      {/* Not purchased - show buy button */}
                      {!hasPurchased && (
                        <button 
                          onClick={() => {
                            Analytics.trackBuyFullBookClicked();
                            setShowPayPalModal(true);
                          }}
                          className="bg-rust hover:bg-rust/90 text-white font-semibold px-5 py-2.5 rounded-xl flex items-center gap-2 transition-colors text-sm"
                        >
                          Buy Full Book
                          <span>→</span>
                        </button>
                      )}
                      
                      {/* Purchased - generating */}
                      {hasPurchased && fullPdfStatus === 'generating' && (
                        <button 
                          disabled
                          className="bg-rust/70 text-white font-semibold px-5 py-2.5 rounded-xl flex items-center gap-2 cursor-not-allowed text-sm"
                        >
                          <ArrowPathIcon className="w-4 h-4 animate-spin" />
                          Generating...
                        </button>
                      )}
                      
                      {/* Purchased - ready to download */}
                      {hasPurchased && fullPdfStatus === 'ready' && (
                        <button 
                          onClick={handleDownloadFullPDF}
                          disabled={isDownloadingFull}
                          className={`bg-rust hover:bg-rust/90 text-white font-semibold px-5 py-2.5 rounded-xl flex items-center gap-2 transition-colors text-sm ${isDownloadingFull ? 'opacity-80 cursor-wait' : ''}`}
                        >
                          {isDownloadingFull ? (
                            <>
                              <ArrowPathIcon className="w-4 h-4 animate-spin" />
                              Downloading...
                            </>
                          ) : (
                            <>
                              <ArrowDownTrayIcon className="w-4 h-4" />
                              Download Full Book
                            </>
                          )}
                        </button>
                      )}
                      
                      {/* Purchased - idle (need to generate) */}
                      {hasPurchased && fullPdfStatus === 'idle' && (
                        <button 
                          onClick={handleGenerateFullPDF}
                          className="bg-rust hover:bg-rust/90 text-white font-semibold px-5 py-2.5 rounded-xl flex items-center gap-2 transition-colors text-sm"
                        >
                          <DocumentArrowDownIcon className="w-4 h-4" />
                          Generate Full Book
                        </button>
                      )}
                      
                      {/* Purchased - error */}
                      {hasPurchased && fullPdfStatus === 'error' && (
                        <button 
                          onClick={handleGenerateFullPDF}
                          className="bg-rust hover:bg-rust/90 text-white font-semibold px-5 py-2.5 rounded-xl flex items-center gap-2 transition-colors text-sm"
                        >
                          <ArrowPathIcon className="w-4 h-4" />
                          Try Again
                        </button>
                      )}
                    </div>
                    
                    {/* Printed Copy Card */}
                    <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-5 flex flex-col">
                      <h4 className="font-display text-lg font-bold text-gray-900 mb-1">
                        Get it printed
                      </h4>
                      <p className="text-gray-500 text-sm mb-1">
                        We'll email you when printed copies are available
                      </p>
                      <div className="flex-1"></div>
                      
                      <button 
                        onClick={() => {
                          Analytics.trackBookNowClicked();
                          setShowLeadModal(true);
                        }}
                        className="bg-white hover:bg-gray-50 text-gray-700 font-semibold px-5 py-2.5 rounded-xl border border-gray-200 flex items-center gap-2 transition-colors text-sm mt-3"
                      >
                        <GiftIcon className="w-4 h-4" />
                        Join the waitlist
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Mobile CTAs - stacked cards */}
            {status === 'complete' && (
              <div className="lg:hidden mt-6 space-y-4">
                {/* Primary CTA Card: Free Sample */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                  <div className="flex items-center gap-4">
                    {/* Book mockup - small */}
                    <div className="shrink-0">
                      <TiltedBookMockup 
                        coverImage={coverImage} 
                        title={bookTitle || 'Yearbook'}
                        size="xs"
                      />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h3 className="font-display text-lg font-bold text-gray-900 mb-1">
                        {pdfStatus === 'ready' ? 'Your sample is ready!' : 'Love what you see?'}
                      </h3>
                      <p className="text-gray-500 text-sm">
                        {pdfStatus === 'ready' 
                          ? 'Download your free PDF' 
                          : `Free PDF with ${stories.length} stories`
                        }
                      </p>
                      {/* Benefit row - mobile */}
                      <div className="flex items-center gap-2 mt-1.5 text-xs text-gray-400">
                        <span className="flex items-center gap-0.5">
                          <span className="text-green-500">✓</span> {stories.length} stories
                        </span>
                        <span>•</span>
                        <span>No signup</span>
                      </div>
                      {pdfError && (
                        <p className="text-sm text-rust mt-2">{pdfError}</p>
                      )}
                      
                      <div className="mt-3">
                        {pdfStatus === 'idle' && (
                          <button 
                            onClick={handleGeneratePDF}
                            className="bg-gradient-to-b from-rust to-rust/90 text-white font-semibold px-5 py-2.5 rounded-xl flex items-center gap-2 transition-colors text-sm w-full justify-center shadow-md shadow-rust/20"
                          >
                            Get Free Sample
                            <span>→</span>
                          </button>
                        )}
                        
                        {pdfStatus === 'generating' && (
                          <button 
                            disabled
                            className="bg-rust/70 text-white font-semibold px-5 py-2.5 rounded-xl flex items-center gap-2 cursor-not-allowed text-sm w-full justify-center"
                          >
                            <ArrowPathIcon className="w-4 h-4 animate-spin" />
                            Generating...
                          </button>
                        )}
                        
                        {pdfStatus === 'ready' && (
                          <button 
                            onClick={async () => {
                              if (isDownloading) return;
                              setIsDownloading(true);
                              setPdfError(null);
                              Analytics.trackPdfDownloaded();
                              try {
                                const filename = `${bookTitle || 'Yearbook'}.pdf`;
                                await downloadPDF(sessionId, filename);
                              } catch (err) {
                                console.error('Download failed:', err);
                                setPdfError('Download failed. Please try again.');
                              } finally {
                                setIsDownloading(false);
                              }
                            }}
                            disabled={isDownloading}
                            className={`bg-gradient-to-b from-rust to-rust/90 text-white font-semibold px-5 py-2.5 rounded-xl flex items-center gap-2 transition-colors text-sm w-full justify-center shadow-md shadow-rust/20 ${isDownloading ? 'opacity-80 cursor-wait' : ''}`}
                          >
                            {isDownloading ? (
                              <>
                                <ArrowPathIcon className="w-4 h-4 animate-spin" />
                                Downloading...
                              </>
                            ) : (
                              <>
                                <ArrowDownTrayIcon className="w-4 h-4" />
                                Download PDF
                              </>
                            )}
                          </button>
                        )}
                        
                        {pdfStatus === 'error' && (
                          <button 
                            onClick={handleGeneratePDF}
                            className="bg-gradient-to-b from-rust to-rust/90 text-white font-semibold px-5 py-2.5 rounded-xl flex items-center gap-2 transition-colors text-sm w-full justify-center shadow-md shadow-rust/20"
                          >
                            <ArrowPathIcon className="w-4 h-4" />
                            Try Again
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Secondary CTAs: Full Book + Printed Copy */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Full Book Card */}
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex flex-col">
                    {/* Most popular badge - mobile */}
                    {!hasPurchased && (
                      <div className="mb-1.5">
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                          ⭐ Most popular
                        </span>
                      </div>
                    )}
                    <h4 className="font-display text-base font-bold text-gray-900 mb-0.5">
                      {hasPurchased 
                        ? (fullPdfStatus === 'ready' ? 'Book ready!' : 'Generating...')
                        : 'Unlock full book'
                      }
                    </h4>
                    <p className="text-gray-500 text-xs">
                      All {chapters.length} chapters
                    </p>
                    {fullPdfError && (
                      <p className="text-xs text-rust mt-1">{fullPdfError}</p>
                    )}
                    <div className="flex-1"></div>
                    
                    <div className="mt-3">
                      {!hasPurchased && (
                        <button 
                          onClick={() => {
                            Analytics.trackBuyFullBookClicked();
                            setShowPayPalModal(true);
                          }}
                          className="bg-rust hover:bg-rust/90 text-white font-semibold px-4 py-2 rounded-xl flex items-center gap-1.5 transition-colors text-xs w-full justify-center"
                        >
                          Buy Full Book
                          <span>→</span>
                        </button>
                      )}
                      
                      {hasPurchased && fullPdfStatus === 'idle' && (
                        <button 
                          onClick={handleGenerateFullPDF}
                          className="bg-rust hover:bg-rust/90 text-white font-semibold px-4 py-2 rounded-xl flex items-center gap-1.5 transition-colors text-xs w-full justify-center"
                        >
                          <DocumentArrowDownIcon className="w-3.5 h-3.5" />
                          Generate
                        </button>
                      )}
                      
                      {hasPurchased && fullPdfStatus === 'generating' && (
                        <button 
                          disabled
                          className="bg-rust/70 text-white font-semibold px-4 py-2 rounded-xl flex items-center gap-1.5 cursor-not-allowed text-xs w-full justify-center"
                        >
                          <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                          Generating...
                        </button>
                      )}
                      
                      {hasPurchased && fullPdfStatus === 'ready' && (
                        <button 
                          onClick={handleDownloadFullPDF}
                          disabled={isDownloadingFull}
                          className={`bg-rust hover:bg-rust/90 text-white font-semibold px-4 py-2 rounded-xl flex items-center gap-1.5 transition-colors text-xs w-full justify-center ${isDownloadingFull ? 'opacity-80 cursor-wait' : ''}`}
                        >
                          {isDownloadingFull ? (
                            <>
                              <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                              Downloading...
                            </>
                          ) : (
                            <>
                              <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                              Download
                            </>
                          )}
                        </button>
                      )}
                      
                      {hasPurchased && fullPdfStatus === 'error' && (
                        <button 
                          onClick={handleGenerateFullPDF}
                          className="bg-rust hover:bg-rust/90 text-white font-semibold px-4 py-2 rounded-xl flex items-center gap-1.5 transition-colors text-xs w-full justify-center"
                        >
                          <ArrowPathIcon className="w-3.5 h-3.5" />
                          Retry
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Printed Copy Card */}
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex flex-col">
                    <h4 className="font-display text-base font-bold text-gray-900 mb-0.5">
                      Get it printed
                    </h4>
                    <p className="text-gray-500 text-xs">
                      Coming soon
                    </p>
                    <div className="flex-1"></div>
                    
                    <div className="mt-3">
                      <button 
                        onClick={() => {
                          Analytics.trackBookNowClicked();
                          setShowLeadModal(true);
                        }}
                        className="bg-white hover:bg-gray-50 text-gray-700 font-semibold px-4 py-2 rounded-xl border border-gray-200 flex items-center gap-1.5 transition-colors text-xs w-full justify-center"
                      >
                        <GiftIcon className="w-3.5 h-3.5" />
                        Join waitlist
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* PayPal Checkout Modal */}
      <PayPalCheckout
        isOpen={showPayPalModal}
        onClose={() => setShowPayPalModal(false)}
        onPurchaseComplete={handlePurchaseComplete}
        sessionId={sessionId}
        bookTitle={bookTitle || 'Yearbook'}
        totalChapters={chapters.length}
      />
      
      {/* Lead Capture Modal */}
      <LeadCaptureModal
        isOpen={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        bookTitle={bookTitle || 'Yearbook'}
      />
      
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav
        activeTab={mobileTab}
        onTabChange={handleMobileTabChange}
      />
      
      {/* Mobile Chapters Drawer */}
      <MobileDrawer
        isOpen={chaptersDrawerOpen}
        onClose={handleCloseChaptersDrawer}
        title="Chapters"
        icon={BookmarkIcon}
      >
        <div className="space-y-1">
          {chapters.map((chapter) => {
            const storyByChapter = {};
            stories.forEach((story, idx) => {
              storyByChapter[story.chapter_index] = idx;
            });
            const isSample = chapter.is_sample;
            const storyIndex = storyByChapter[chapter.index];
            const isActive = chapter.index === currentStory?.chapter_index;
            const hasStory = storyIndex !== undefined;
            
            return (
              <button
                key={chapter.index}
                onClick={() => hasStory && handleMobileStorySelect(storyIndex)}
                disabled={!hasStory}
                className={`
                  w-full text-left px-4 py-3 rounded-xl transition-all duration-200
                  ${isActive 
                    ? 'bg-rust/10 border-l-4 border-rust' 
                    : hasStory 
                      ? 'hover:bg-parchment/50 border-l-4 border-transparent' 
                      : 'opacity-40'
                  }
                `}
              >
                <div className="flex items-center gap-3">
                  <span className={`font-display text-sm ${isActive ? 'text-rust font-semibold' : 'text-sepia'}`}>
                    {chapter.index + 1}
                  </span>
                  {chapter.title_emoji && (
                    <span className="text-lg">{chapter.title_emoji}</span>
                  )}
                  <span className={`flex-1 text-sm truncate ${isActive ? 'text-rust font-medium' : isSample ? 'text-ink' : 'text-sepia'}`}>
                    {chapter.title}
                  </span>
                  {isSample && !isActive && (
                    <SparklesIcon className="w-4 h-4 text-rust/50 shrink-0" />
                  )}
                </div>
                {!hasStory && (
                  <p className="text-[10px] text-sepia/60 mt-0.5 pl-8">Available in full book</p>
                )}
              </button>
            );
          })}
        </div>
        
        {/* Legend */}
        <div className="mt-6 pt-4 border-t border-parchment/50">
          <div className="flex items-center justify-center gap-4 text-xs text-sepia">
            <div className="flex items-center gap-1.5">
              <SparklesIcon className="w-3 h-3 text-rust/60" />
              <span>Featured</span>
            </div>
            <span className="text-parchment">·</span>
            <div className="flex items-center gap-1.5">
              <span className="text-sepia/50">Faded</span>
              <span>= Full book only</span>
            </div>
          </div>
        </div>
      </MobileDrawer>
      
      {/* Mobile Cast Drawer */}
      <MobileDrawer
        isOpen={castDrawerOpen}
        onClose={handleCloseCastDrawer}
        title="Cast of Characters"
        icon={UserGroupIcon}
      >
        {characters.length > 0 ? (
          <div className="space-y-2">
            {(() => {
              const maxStoryCount = Math.max(...characters.map(c => c.story_count || 0));
              return characters.map((char, index) => {
                const isTopContributor = char.story_count === maxStoryCount && maxStoryCount > 1;
                const isExpanded = expandedCharacter === index;
                
                return (
                  <div 
                    key={index}
                    className={`bg-white rounded-xl border transition-all duration-300 ease-out
                               ${isExpanded 
                                 ? 'border-gold/40 shadow-md' 
                                 : 'border-parchment/30'}`}
                  >
                    <button
                      onClick={() => setExpandedCharacter(isExpanded ? null : index)}
                      className="w-full flex items-center gap-3 p-3 text-left"
                    >
                      <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getAvatarGradient(char.name)} 
                                      flex items-center justify-center text-white font-bold text-sm shrink-0 shadow-sm`}>
                        {getInitials(char.name)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <span className="font-display text-sm font-semibold text-ink block truncate">
                          {char.name}
                        </span>
                        {char.traits && char.traits.length > 0 && (
                          <div className="flex items-center gap-1 mt-1">
                            {char.traits.slice(0, 2).map((trait, i) => (
                              <span 
                                key={i}
                                className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full border ${getTraitColor(trait)}`}
                              >
                                {trait}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {isTopContributor && (
                        <span className="flex items-center gap-0.5 text-[9px] font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full border border-amber-200 shrink-0">
                          <StarIcon className="w-2.5 h-2.5" />
                          MVP
                        </span>
                      )}
                      
                      <ChevronDownIcon 
                        className={`w-5 h-5 text-sepia/40 shrink-0 transition-transform duration-200
                                   ${isExpanded ? 'rotate-180' : ''}`} 
                      />
                    </button>
                    
                    {isExpanded && (
                      <div className="px-3 pb-3 pt-0 border-t border-parchment/20">
                        {char.signature_quote && (
                          <p className="font-handwritten text-sm text-sepia/70 italic mt-2 pl-3 border-l-2 border-gold/40">
                            "{char.signature_quote}"
                          </p>
                        )}
                        
                        <p className="text-xs text-sepia/80 leading-relaxed mt-2">{char.bio}</p>
                        
                        <div className="flex items-center gap-2 mt-3 pt-2 border-t border-parchment/20">
                          <span className="text-[10px] text-sepia/50">
                            {char.story_count} {char.story_count === 1 ? 'story' : 'stories'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        ) : charactersLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <ArrowPathIcon className="w-8 h-8 text-gold animate-spin mb-3" />
            <p className="text-sm text-sepia">Building character profiles...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12">
            <UserGroupIcon className="w-12 h-12 text-sepia/30 mb-3" />
            <p className="text-sm text-sepia/60">Characters will appear after stories load</p>
          </div>
        )}
      </MobileDrawer>
    </div>
  );
}

